import { Button, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { LayoutDashboardIcon } from 'lucide-react';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardPresets } from '@/entities/settings';
import { DashboardPresetItem } from './DashboardPresetItem';

type Props = {
  /**
   * Пользователь сменил применённый пресет: его идентификатор либо `null`, если пресет снят.
   * У закреплённого пресета есть собственный адрес, поэтому переход делает страница — сам
   * поповер не навигирует.
   */
  onActivePresetChange: (presetId: string | null) => void;
};

/**
 * Список пользовательских дашбордов-пресетов: выбор дашборда и снятие применённого.
 * Действия над самим дашбордом ведёт {@link DashboardPresetActions} в панели страницы.
 */
export const DashboardPresetsPopover: FC<Props> = ({ onActivePresetChange }) => {
  const { t } = useTranslation('qualityDashboard');
  const { presets, activePreset, apply, clearActive } = useDashboardPresets();

  const [isOpen, setIsOpen] = useState(false);

  const handleApply = useCallback(
    (presetId: string) => {
      apply(presetId);
      onActivePresetChange(presetId);
      setIsOpen(false);
    },
    [apply, onActivePresetChange],
  );

  const handleClearActive = useCallback(() => {
    clearActive();
    onActivePresetChange(null);
  }, [clearActive, onActivePresetChange]);

  const triggerLabel = activePreset?.name ?? t('presets.triggerLabel');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={activePreset ? 'outline' : 'secondary'}
          size="sm"
          icon={<LayoutDashboardIcon className="size-4" />}
          aria-label={t('presets.triggerLabel')}
          className="max-w-64"
        >
          <span className="truncate">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 flex flex-col gap-3">
        <h4 className="font-medium text-sm">{t('presets.title')}</h4>

        {presets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">{t('presets.empty')}</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-72 overflow-auto">
            {presets.map((preset) => (
              <DashboardPresetItem
                key={preset.id}
                preset={preset}
                isActive={preset.id === activePreset?.id}
                onApply={() => handleApply(preset.id)}
                onClearActive={handleClearActive}
              />
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground border-t pt-3">{t('presets.createHint')}</p>
      </PopoverContent>
    </Popover>
  );
};
